import { useState, useRef } from 'react';
import { authHeader, clearAuthToken } from '../lib/api.js';

/**
 * KYC file uploader.
 * Three modes: drop / pick a PDF/DOCX, paste raw text, or — when a scanned
 * PDF is detected — offer browser-side OCR.
 * Calls onExtracted({ text, pages, fileType, fileName, looksScanned }) when ready.
 */
export default function Uploader({ onExtracted, disabled = false }) {
  const [mode, setMode] = useState('upload'); // 'upload' | 'paste'
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(false);

  // OCR-offer state — set when the server flags a scanned PDF.
  // { file: File, pages: number|null, charsExtracted: number }
  const [ocrOffer, setOcrOffer] = useState(null);
  // OCR-in-progress state.
  // { stage, pageIndex?, totalPages?, status?, progress? }
  const [ocrProgress, setOcrProgress] = useState(null);

  const inputRef = useRef(null);

  const MAX_BYTES = 3.5 * 1024 * 1024;
  const MIN_TEXT_FOR_PIPELINE = 200; // below this, we suggest OCR for PDFs

  async function handleFile(file) {
    if (!file) return;
    setError(null);
    setOcrOffer(null);
    setOcrProgress(null);
    if (file.size > MAX_BYTES) {
      setError(`File is ${Math.round(file.size / 1024 / 1024)} MB. Max 3.5 MB.`);
      return;
    }
    setBusy(true);
    try {
      const base64 = await fileToBase64(file);
      const res = await fetch('/api/extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          base64,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401) clearAuthToken();
        throw new Error(data.error || `Extract failed (${res.status})`);
      }
      const text = data.text || '';
      const isPdf = data.fileType === 'pdf' || file.name.toLowerCase().endsWith('.pdf');

      // Scanned PDF (or extremely low-content PDF) → offer OCR instead of erroring.
      if (isPdf && (data.looksScanned || text.length < MIN_TEXT_FOR_PIPELINE)) {
        setOcrOffer({ file, pages: data.pages, charsExtracted: text.length });
        return;
      }
      if (text.length < 50) {
        throw new Error('No text could be extracted. Try pasting the content manually.');
      }

      onExtracted({
        text,
        pages: data.pages,
        fileType: data.fileType,
        fileName: file.name,
        looksScanned: data.looksScanned,
      });
    } catch (err) {
      setError(err.message || 'Extraction failed');
    } finally {
      setBusy(false);
    }
  }

  async function runOcr() {
    if (!ocrOffer?.file) return;
    setError(null);
    setOcrProgress({ stage: 'init', status: 'Loading OCR engine…' });
    try {
      const { ocrPdfFile } = await import('../lib/ocr.js');
      const result = await ocrPdfFile(ocrOffer.file, (update) => setOcrProgress(update));
      if (!result.text || result.text.replace(/--- PAGE \d+ ---/g, '').trim().length < 50) {
        throw new Error('OCR completed but found very little text. The file may be too low quality.');
      }
      onExtracted({
        text: result.text,
        pages: result.pages,
        fileType: result.fileType,
        fileName: ocrOffer.file.name,
        looksScanned: true,
      });
    } catch (err) {
      setError(`OCR failed: ${err?.message || err}`);
      setOcrProgress(null);
    }
  }

  function dismissOcr() {
    setOcrOffer(null);
    setOcrProgress(null);
  }

  function handlePasteSubmit() {
    setError(null);
    const text = pasted.trim();
    if (text.length < 100) {
      setError('Paste at least a few paragraphs of KYC content.');
      return;
    }
    onExtracted({
      text,
      pages: null,
      fileType: 'paste',
      fileName: 'Pasted content',
      looksScanned: false,
    });
  }

  function onDrop(e) {
    e.preventDefault();
    setHover(false);
    if (disabled || busy) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }

  // ─── OCR offer card replaces the upload UI when triggered ──────────────
  if (ocrOffer && !ocrProgress) {
    return (
      <div className="rounded-lg border border-amber-900/40 bg-amber-900/10 p-5">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-300">
          Scanned PDF detected
        </div>
        <p className="mb-4 text-sm text-amber-100">
          <span className="font-medium">{ocrOffer.file.name}</span> doesn't appear to have a
          readable text layer ({ocrOffer.charsExtracted} chars in {ocrOffer.pages || '?'}{' '}
          pages). Run browser-based OCR to extract text, or switch to paste mode.
        </p>
        <p className="mb-4 text-xs text-amber-200/80">
          OCR runs entirely in your browser — no document data leaves your computer.
          First use downloads ~12 MB of OCR data; subsequent runs are cached. Takes 20–60 seconds per page.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={runOcr}
            className="rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy-900 transition hover:brightness-110"
          >
            Run OCR on {ocrOffer.pages || '?'} page{ocrOffer.pages === 1 ? '' : 's'}
          </button>
          <button
            type="button"
            onClick={() => { dismissOcr(); setMode('paste'); }}
            className="rounded-md border border-amber-300 px-4 py-2 text-sm text-amber-200 hover:bg-amber-900/30"
          >
            Paste text instead
          </button>
          <button
            type="button"
            onClick={dismissOcr}
            className="rounded-md border border-navy-700 px-4 py-2 text-sm text-slate-300 hover:border-gold"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ─── OCR progress card ─────────────────────────────────────────────────
  if (ocrProgress) {
    return <OcrProgress progress={ocrProgress} fileName={ocrOffer?.file?.name} />;
  }

  // ─── Default upload / paste UI ─────────────────────────────────────────
  return (
    <div className="rounded-lg border border-navy-700 bg-navy-800 p-5">
      <div className="mb-4 inline-flex rounded-md border border-navy-700 bg-navy-900 p-1 text-xs">
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={`rounded px-3 py-1.5 font-medium transition ${
            mode === 'upload' ? 'bg-gold text-navy-900' : 'text-slate-300 hover:text-white'
          }`}
        >
          Upload PDF / DOCX
        </button>
        <button
          type="button"
          onClick={() => setMode('paste')}
          className={`rounded px-3 py-1.5 font-medium transition ${
            mode === 'paste' ? 'bg-gold text-navy-900' : 'text-slate-300 hover:text-white'
          }`}
        >
          Paste Text
        </button>
      </div>

      {mode === 'upload' && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled && !busy) setHover(true);
          }}
          onDragLeave={() => setHover(false)}
          onDrop={onDrop}
          onClick={() => !busy && !disabled && inputRef.current?.click()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-10 text-center transition ${
            hover ? 'border-gold bg-navy-900/50' : 'border-navy-700 bg-navy-900/30'
          } ${disabled || busy ? 'cursor-not-allowed opacity-50' : 'hover:border-gold'}`}
        >
          <div className="mb-3 text-3xl">{busy ? '...' : '+'}</div>
          <div className="text-sm font-medium text-white">
            {busy ? 'Extracting text...' : 'Drop a KYC file here, or click to browse'}
          </div>
          <div className="mt-1 text-xs text-slate-400">
            PDF or DOCX, up to 3.5 MB. Scanned PDFs prompt for OCR after upload.
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            disabled={disabled || busy}
          />
        </div>
      )}

      {mode === 'paste' && (
        <div>
          <textarea
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder="Paste the KYC/CDD file content here. Include customer details, ownership structure, screening results, and source-of-funds notes."
            className="prose-mono h-56 w-full rounded-md border border-navy-700 bg-navy-900 p-3 text-slate-200 placeholder:text-slate-500 focus:border-gold focus:outline-none"
            disabled={disabled || busy}
          />
          <div className="mt-3 flex items-center justify-between">
            <div className="text-xs text-slate-400">{pasted.length.toLocaleString()} chars</div>
            <button
              type="button"
              onClick={handlePasteSubmit}
              disabled={disabled || busy || pasted.trim().length < 100}
              className="rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy-900 disabled:opacity-50"
            >
              Use This Text
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 rounded-md border border-red-900/50 bg-red-900/20 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      )}

      <div className="mt-4 rounded-md border border-amber-900/40 bg-amber-900/10 px-3 py-2 text-xs text-amber-200">
        <strong>Confidentiality reminder:</strong> Do not upload real customer PII without an
        appropriate data-processing addendum in place. Use redacted or synthetic content for
        evaluation.
      </div>
    </div>
  );
}

function OcrProgress({ progress, fileName }) {
  const { stage, pageIndex, totalPages, status, progress: pct } = progress || {};
  const pageBar = totalPages ? ((pageIndex || 0) - (stage === 'page' ? 0.5 : 0)) / totalPages : 0;
  const overall = Math.max(0, Math.min(1, pageBar + ((pct || 0) / (totalPages || 1)) * 0.5));

  return (
    <div className="rounded-lg border border-navy-700 bg-navy-800 p-5">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gold">
        Running OCR
      </div>
      <p className="mb-3 text-sm text-white">{fileName}</p>
      <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-navy-900">
        <div
          className="h-full bg-gold transition-all"
          style={{ width: `${Math.round(overall * 100)}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-slate-300">
        <span>
          {stage === 'init' && 'Loading OCR engine…'}
          {stage === 'page' && `Page ${pageIndex} of ${totalPages} · ${status || ''}`}
          {stage === 'ocr' && `${status || 'Processing'}${pct ? ` (${Math.round(pct * 100)}%)` : ''}`}
          {stage === 'done' && 'Finalising…'}
        </span>
        <span className="text-slate-500">
          OCR runs in your browser. Don't close the tab.
        </span>
      </div>
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}
